// Numeric trend series for an item's Intensity tab (D-IR-3/D-IR-4). Delegates to
// the existing charts/series.ts builders and slices to the item; supplies small
// local builders for the few rated types series.ts doesn't cover. Kept separate
// so the global Charts page (series.ts / CHART_TYPES) is not altered.

import type { MedicalEvent } from "@medassist/shared";
import {
  symptomFollowUpKey,
  buildSymptomActivity,
  buildSymptomDailySeries,
  DEFAULT_SYMPTOM_AUTO_CLOSE_DAYS,
  todayInTimezone,
} from "@medassist/shared";
import type { ItemIdentity } from "../../lib/itemReport.js";
import {
  buildVitalSeries,
  buildSleepSeries,
  buildMoodSeries,
  buildHydrationSeries,
  type ChartSeries,
  type DataPoint,
} from "../charts/series.js";

function sortedPoints(points: DataPoint[]): DataPoint[] {
  return points.sort((a, b) => a.x - b.x);
}

// Orders a series map by a priority list (primary metric first per D-IR-4),
// appending any remaining entries in map-iteration order.
function orderSeries(map: Map<string, ChartSeries>, priority: string[]): ChartSeries[] {
  const result: ChartSeries[] = [];
  const seen = new Set<string>();
  for (const key of priority) {
    const s = map.get(key);
    if (s) {
      result.push(s);
      seen.add(key);
    }
  }
  for (const [key, s] of map) {
    if (!seen.has(key)) result.push(s);
  }
  return result;
}

function buildStressSeries(events: MedicalEvent[]): ChartSeries[] {
  const points: DataPoint[] = [];
  for (const e of events) {
    const p = e.payload as Record<string, unknown>;
    const sev = Number(p.severity);
    if (isNaN(sev)) continue;
    const label = new Date(e.occurredAt).toLocaleDateString();
    points.push({ x: new Date(e.occurredAt).getTime(), y: sev, label: `${label}: ${sev}/10` });
  }
  if (points.length === 0) return [];
  return [
    {
      key: "stress_severity",
      name: "Stress severity (1–10)",
      points: sortedPoints(points),
      yLabel: "1–10",
      color: "#ef4444",
      unit: "/10",
    },
  ];
}

function buildExerciseSeries(events: MedicalEvent[]): ChartSeries[] {
  const rpePts: DataPoint[] = [];
  const durPts: DataPoint[] = [];
  for (const e of events) {
    const p = e.payload as Record<string, unknown>;
    const x = new Date(e.occurredAt).getTime();
    const label = new Date(e.occurredAt).toLocaleDateString();
    const rpe = Number(p.perceivedExertionRpe);
    if (!isNaN(rpe)) rpePts.push({ x, y: rpe, label: `${label}: RPE ${rpe}/10` });
    const dur = Number(p.durationMin);
    if (!isNaN(dur)) durPts.push({ x, y: dur, label: `${label}: ${dur} min` });
  }
  const result: ChartSeries[] = [];
  if (rpePts.length)
    result.push({
      key: "exercise_rpe",
      name: "Perceived exertion (1–10)",
      points: sortedPoints(rpePts),
      yLabel: "1–10",
      color: "#f97316",
      unit: "/10",
    });
  if (durPts.length)
    result.push({
      key: "exercise_duration",
      name: "Duration (min)",
      points: sortedPoints(durPts),
      yLabel: "minutes",
      color: "#6366f1",
      unit: "min",
    });
  return result;
}

function buildBowelPainSeries(events: MedicalEvent[]): ChartSeries[] {
  const points: DataPoint[] = [];
  for (const e of events) {
    const p = e.payload as Record<string, unknown>;
    if (p.painPresent !== true) continue;
    const sev = Number(p.painSeverity);
    if (isNaN(sev)) continue;
    const label = new Date(e.occurredAt).toLocaleDateString();
    points.push({ x: new Date(e.occurredAt).getTime(), y: sev, label: `${label}: pain ${sev}/10` });
  }
  if (points.length === 0) return [];
  return [
    {
      key: "bowel_pain",
      name: "Pain severity (1–10)",
      points: sortedPoints(points),
      yLabel: "1–10",
      color: "#ef4444",
      unit: "/10",
    },
  ];
}

const VITAL_PRIORITY = ["bp_systolic", "bp_diastolic", "hr", "temp", "spo2", "glucose", "respiratory_rate", "weight"];
const SLEEP_PRIORITY = ["sleep_quality", "sleep_hours"];
const MOOD_PRIORITY = ["mood", "anxiety", "energy", "focus", "irritability"];

/**
 * Daily-resampled step series for a symptom/headache item (D-IE-15): one
 * point per local calendar day from the earliest active span through today,
 * carrying each check-in's severity forward while active and sitting flat at
 * 0 while resolved — no diagonal ramp down to the next onset.
 */
function buildSymptomActivitySeries(
  item: ItemIdentity,
  events: MedicalEvent[],
  timezone: string,
  autoCloseDays: number
): ChartSeries[] {
  const activity = buildSymptomActivity(events, timezone, autoCloseDays);
  if (activity.spans.length === 0) return [];

  const startDate = activity.spans[0].startDate;
  const endDate = todayInTimezone(timezone);
  const daily = buildSymptomDailySeries(activity, startDate, endDate);

  const points: DataPoint[] = daily.map(({ date, severity }) => {
    const x = new Date(`${date}T12:00:00Z`).getTime();
    const localeDate = new Date(`${date}T12:00:00Z`).toLocaleDateString(undefined, { timeZone: "UTC" });
    const label = severity === 0 ? `${localeDate}: resolved` : `${localeDate}: ${severity}/10`;
    return { x, y: severity, label };
  });

  return [
    {
      key: symptomFollowUpKey(item.label),
      name: item.label,
      points: sortedPoints(points),
      yLabel: "severity (1–10)",
      color: "#ef4444",
      unit: "/10",
    },
  ];
}

/**
 * Numeric trend series for an item (primary first, D-IR-4). Empty array means
 * the item has no plottable metric, so the Intensity tab should be hidden.
 * `events` must already be item-scoped + filtered (deleted/missed dropped) —
 * see `itemEvents` in lib/itemReport.ts.
 */
export function buildItemSeries(
  item: ItemIdentity,
  events: MedicalEvent[],
  opts?: { timezone?: string; autoCloseDays?: number }
): ChartSeries[] {
  switch (item.eventType) {
    case "symptom":
    case "headache": {
      const timezone = opts?.timezone ?? Intl.DateTimeFormat().resolvedOptions().timeZone;
      const autoCloseDays = opts?.autoCloseDays ?? DEFAULT_SYMPTOM_AUTO_CLOSE_DAYS;
      return buildSymptomActivitySeries(item, events, timezone, autoCloseDays);
    }
    case "vital_sign":
      return orderSeries(buildVitalSeries(events), VITAL_PRIORITY);
    case "sleep":
      return orderSeries(buildSleepSeries(events), SLEEP_PRIORITY);
    case "mood":
      return orderSeries(buildMoodSeries(events), MOOD_PRIORITY);
    case "hydration": {
      const timezone = opts?.timezone ?? Intl.DateTimeFormat().resolvedOptions().timeZone;
      return orderSeries(buildHydrationSeries(events, timezone), ["hydration"]);
    }
    case "stress_event":
      return buildStressSeries(events);
    case "exercise":
      return buildExerciseSeries(events);
    case "bowel_movement":
      return buildBowelPainSeries(events);
    default:
      return [];
  }
}
